package com.partejar.app.feature.gestantes.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.partejar.app.feature.gestantes.data.GestanteListStage
import com.partejar.app.feature.gestantes.data.GestantesDataSource
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class GestantesViewModel private constructor(
    private val dataSource: GestantesDataSource,
    private val scopeOverride: CoroutineScope?,
    private val debounceMillis: Long,
    @Suppress("UNUSED_PARAMETER") testableConstructor: Unit
) : ViewModel() {
    constructor(dataSource: GestantesDataSource) : this(dataSource, null, DEFAULT_DEBOUNCE_MILLIS, Unit)

    internal constructor(
        dataSource: GestantesDataSource,
        scope: CoroutineScope,
        debounceMillis: Long = DEFAULT_DEBOUNCE_MILLIS
    ) : this(dataSource, scope, debounceMillis, Unit)

    private val scope get() = scopeOverride ?: viewModelScope
    private val _uiState = MutableStateFlow<GestantesUiState>(GestantesUiState.Loading)
    val uiState: StateFlow<GestantesUiState> = _uiState.asStateFlow()
    private val _query = MutableStateFlow("")
    val query: StateFlow<String> = _query.asStateFlow()
    private val _selectedStages = MutableStateFlow(DefaultGestanteStages)
    val selectedStages: StateFlow<Set<GestanteListStage>> = _selectedStages.asStateFlow()

    private var currentPage = 0
    private var searchJob: Job? = null
    private var loadMoreJob: Job? = null

    init {
        loadFirstPage()
    }

    fun onQueryChanged(value: String) {
        _query.value = value
        loadMoreJob?.cancel()
        searchJob?.cancel()
        searchJob = scope.launch {
            delay(debounceMillis)
            loadFirstPageNow()
        }
    }

    fun toggleStage(stage: GestanteListStage) {
        _selectedStages.value = setOf(stage)
        loadMoreJob?.cancel()
        searchJob?.cancel()
        loadFirstPage()
    }

    fun retry() = loadFirstPage()

    fun loadNextPage() {
        val current = _uiState.value as? GestantesUiState.Content ?: return
        if (!current.hasNextPage || current.isLoadingNextPage) return
        _uiState.value = current.copy(isLoadingNextPage = true, nextPageError = null)
        loadMoreJob = scope.launch {
            try {
                val next = dataSource.loadPage(
                    page = currentPage + 1,
                    size = PAGE_SIZE,
                    name = normalizedQuery(),
                    statuses = _selectedStages.value
                )
                currentPage = next.page
                _uiState.value = current.copy(
                    items = (current.items + next.items).distinctBy { it.id },
                    totalElements = next.totalElements,
                    hasNextPage = !next.isLast,
                    isLoadingNextPage = false,
                    nextPageError = null
                )
            } catch (error: CancellationException) {
                throw error
            } catch (_: Throwable) {
                _uiState.value = current.copy(
                    isLoadingNextPage = false,
                    nextPageError = "Não foi possível carregar mais gestantes."
                )
            }
        }
    }

    private fun loadFirstPage() {
        loadMoreJob?.cancel()
        searchJob?.cancel()
        searchJob = scope.launch { loadFirstPageNow() }
    }

    private suspend fun loadFirstPageNow() {
        val previous = _uiState.value as? GestantesUiState.Content
        _uiState.value = previous?.copy(isRefreshing = true, nextPageError = null)
            ?: GestantesUiState.Loading
        try {
            val first = dataSource.loadPage(
                page = 0,
                size = PAGE_SIZE,
                name = normalizedQuery(),
                statuses = _selectedStages.value
            )
            currentPage = 0
            _uiState.value = if (first.items.isEmpty()) {
                GestantesUiState.Empty(hasActiveSearch = normalizedQuery() != null)
            } else {
                GestantesUiState.Content(
                    items = first.items,
                    totalElements = first.totalElements,
                    hasNextPage = !first.isLast
                )
            }
        } catch (error: CancellationException) {
            throw error
        } catch (_: Throwable) {
            _uiState.value = GestantesUiState.Error("Não foi possível carregar as gestantes.")
        }
    }

    private fun normalizedQuery(): String? = _query.value.trim().takeIf { it.isNotEmpty() }

    companion object {
        internal const val PAGE_SIZE = 20
        internal const val DEFAULT_DEBOUNCE_MILLIS = 300L
    }
}
